import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'
import FHIR from 'fhirclient'
import type { FhirClient, FhirPatient, FhirPractitioner, SmartLaunchConfig } from '@/types'
import { SMART_CONFIG } from '@/types'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export const useAuthStore = defineStore('auth', () => {
  // State
  const isLoading = ref(true)
  const error = ref<string | null>(null)
  const client = ref<FhirClient | null>(null)
  const patient = ref<FhirPatient | null>(null)
  const practitioner = ref<FhirPractitioner | null>(null)
  const accessToken = ref<string | null>(null)
  const tokenExpiry = ref<Date | null>(null)

  // CDS backend session token (separate from Epic access token)
  const cdsToken = ref<string | null>(null)

  // Computed
  const isAuthenticated = computed(() => {
    return !!cdsToken.value && !isTokenExpired.value
  })

  const isTokenExpired = computed(() => {
    if (!tokenExpiry.value) return true
    return new Date() >= tokenExpiry.value
  })

  const patientName = computed(() => {
    if (!patient.value?.name?.[0]) return 'Unknown Patient'
    const name = patient.value.name[0]
    const given = name.given?.join(' ') || ''
    const family = name.family || ''
    return `${given} ${family}`.trim() || 'Unknown Patient'
  })

  const practitionerName = computed(() => {
    if (!practitioner.value?.name?.[0]) return 'Unknown Provider'
    const name = practitioner.value.name[0]
    const prefix = name.prefix?.join(' ') || ''
    const given = name.given?.join(' ') || ''
    const family = name.family || ''
    return `${prefix} ${given} ${family}`.trim() || 'Unknown Provider'
  })

  const patientContext = computed(() => ({
    id: patient.value?.id || null,
    name: patientName.value,
    birthDate: patient.value?.birthDate || null,
    gender: patient.value?.gender || null,
  }))

  // Actions
  async function initializeAuth(): Promise<void> {
    isLoading.value = true
    error.value = null

    try {
      // Check for existing session
      await restoreSession()
    } catch (err) {
      console.error('Auth initialization error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to initialize authentication'
    } finally {
      isLoading.value = false
    }
  }

  async function launchSmart(config?: Partial<SmartLaunchConfig>): Promise<void> {
    isLoading.value = true
    error.value = null

    const launchConfig = { ...SMART_CONFIG, ...config }

    try {
      // Get ISS from URL params if available (EHR launch)
      const urlParams = new URLSearchParams(window.location.search)
      const iss = urlParams.get('iss') || launchConfig.iss

      if (!iss) {
        throw new Error('No FHIR server (iss) specified. This app must be launched from an EHR.')
      }

      // Initiate SMART authorization
      await FHIR.oauth2.authorize({
        iss: iss,
        clientId: launchConfig.clientId,
        scope: launchConfig.scope,
        redirectUri: launchConfig.redirectUri,
        launch: urlParams.get('launch') || undefined,
      })
    } catch (err) {
      console.error('SMART launch error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to launch SMART authorization'
      isLoading.value = false
      throw err
    }
  }

  async function handleCallback(): Promise<boolean> {
    isLoading.value = true
    error.value = null

    try {
      // Complete the OAuth flow and get the FHIR client
      const fhirClient = await FHIR.oauth2.ready()

      if (!fhirClient) {
        throw new Error('Failed to complete SMART authorization')
      }

      // Store the client
      client.value = fhirClient as unknown as FhirClient

      // Extract and store the access token
      const state = fhirClient.state
      accessToken.value = state.tokenResponse?.access_token || null

      // Calculate token expiry
      if (state.tokenResponse?.expires_in) {
        const expiresIn = state.tokenResponse.expires_in * 1000
        tokenExpiry.value = new Date(Date.now() + expiresIn)
      }

      // Store state for session restoration
      sessionStorage.setItem('SMART_CLIENT_STATE', JSON.stringify(state))

      // Fetch patient and practitioner context from Epic FHIR
      await fetchPatientContext()
      await fetchPractitionerContext()

      // Register with CDS backend to get a CDS session token
      await registerWithBackend()

      // Sync FHIR data to populate inbox
      await syncFhirData()

      return true
    } catch (err) {
      console.error('OAuth callback error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to complete authorization'
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function registerWithBackend(): Promise<void> {
    if (!accessToken.value) {
      throw new Error('No access token available for backend registration')
    }

    const state = client.value ? (client.value as any).state : null
    const scopes = state?.tokenResponse?.scope?.split(' ') || []

    try {
      const response = await axios.post(`${API_BASE}/epic/register-session`, {
        access_token: accessToken.value,
        patient_id: patient.value?.id || null,
        practitioner_id: practitioner.value?.id || null,
        practitioner_name: practitionerName.value,
        expires_in: state?.tokenResponse?.expires_in || 3600,
        scopes: scopes,
      })

      cdsToken.value = response.data.session_token
      if (response.data.expires_at) {
        tokenExpiry.value = new Date(response.data.expires_at)
      }

      // Store CDS token for session restoration
      sessionStorage.setItem('CDS_SESSION_TOKEN', cdsToken.value!)

      console.log('CDS backend session registered successfully')
    } catch (err) {
      console.error('Failed to register with CDS backend:', err)
      // Don't throw - we can still function with Epic token directly
    }
  }

  async function syncFhirData(): Promise<void> {
    if (!cdsToken.value) {
      console.warn('No CDS token - skipping FHIR sync')
      return
    }

    try {
      const response = await axios.post(
        `${API_BASE}/epic/sync`,
        { force: false },
        {
          headers: { Authorization: `Bearer ${cdsToken.value}` },
        }
      )
      console.log('FHIR sync result:', response.data)
    } catch (err) {
      console.error('FHIR sync failed:', err)
      // Non-fatal - inbox will just be empty
    }
  }

  async function restoreSession(): Promise<boolean> {
    // Try to restore CDS token from sessionStorage
    const storedCdsToken = sessionStorage.getItem('CDS_SESSION_TOKEN')
    const storedExpiresAt = sessionStorage.getItem('CDS_TOKEN_EXPIRES_AT')

    // Backend OAuth flow: CDS token stored directly
    if (storedCdsToken) {
      // Check expiry
      if (storedExpiresAt) {
        const expiry = new Date(storedExpiresAt)
        if (Date.now() >= expiry.getTime()) {
          clearSession()
          return false
        }
        tokenExpiry.value = expiry
      }
      cdsToken.value = storedCdsToken
      isLoading.value = false
      return true
    }

    // Fallback: try fhirclient.js SMART state
    const storedState = sessionStorage.getItem('SMART_CLIENT_STATE')
    if (!storedState) {
      return false
    }

    try {
      const state = JSON.parse(storedState)

      if (state.tokenResponse?.expires_in) {
        const tokenCreated = state.tokenResponse.created || Date.now()
        const expiresAt = tokenCreated + state.tokenResponse.expires_in * 1000

        if (Date.now() >= expiresAt) {
          clearSession()
          return false
        }

        tokenExpiry.value = new Date(expiresAt)
      }

      const fhirClient = await FHIR.oauth2.ready()
      client.value = fhirClient as unknown as FhirClient
      accessToken.value = state.tokenResponse?.access_token || null

      await fetchPatientContext()
      await fetchPractitionerContext()

      if (!cdsToken.value && accessToken.value) {
        await registerWithBackend()
      }

      return true
    } catch (err) {
      console.error('Session restoration error:', err)
      clearSession()
      return false
    }
  }

  async function fetchPatientContext(): Promise<void> {
    if (!client.value) return

    try {
      const patientData = await client.value.patient.read()
      patient.value = patientData
    } catch (err) {
      console.error('Failed to fetch patient context:', err)
      // Patient context is optional in some scenarios
    }
  }

  async function fetchPractitionerContext(): Promise<void> {
    if (!client.value) return

    try {
      const userData = await client.value.user.read()
      if (userData.resourceType === 'Practitioner') {
        practitioner.value = userData as FhirPractitioner
      }
    } catch (err) {
      console.error('Failed to fetch practitioner context:', err)
      // User context is optional
    }
  }

  async function refreshToken(): Promise<boolean> {
    // Token refresh logic would go here
    // For now, return false to indicate refresh not supported
    return false
  }

  function setSession(
    sessionToken: string,
    _patientId: string,
    _providerId: string,
    expiresAt: string,
  ): void {
    cdsToken.value = sessionToken
    tokenExpiry.value = expiresAt ? new Date(expiresAt) : new Date(Date.now() + 3600000)
    isLoading.value = false
    error.value = null

    // Store for session restoration
    sessionStorage.setItem('CDS_SESSION_TOKEN', sessionToken)
    if (expiresAt) {
      sessionStorage.setItem('CDS_TOKEN_EXPIRES_AT', expiresAt)
    }

    console.log('CDS session set from backend OAuth callback')
  }

  function clearSession(): void {
    client.value = null
    patient.value = null
    practitioner.value = null
    accessToken.value = null
    cdsToken.value = null
    tokenExpiry.value = null
    error.value = null
    sessionStorage.removeItem('SMART_CLIENT_STATE')
    sessionStorage.removeItem('CDS_SESSION_TOKEN')
    sessionStorage.removeItem('CDS_TOKEN_EXPIRES_AT')
  }

  function logout(): void {
    clearSession()
    // Redirect to launch page
    window.location.href = '/launch'
  }

  function getAuthHeader(): Record<string, string> {
    // Prefer CDS session token for backend API calls
    if (cdsToken.value) {
      return { Authorization: `Bearer ${cdsToken.value}` }
    }
    // Fallback to Epic access token
    if (accessToken.value) {
      return { Authorization: `Bearer ${accessToken.value}` }
    }
    return {}
  }

  return {
    // State
    isLoading,
    error,
    client,
    patient,
    practitioner,
    accessToken,
    cdsToken,
    tokenExpiry,

    // Computed
    isAuthenticated,
    isTokenExpired,
    patientName,
    practitionerName,
    patientContext,

    // Actions
    initializeAuth,
    launchSmart,
    handleCallback,
    setSession,
    registerWithBackend,
    syncFhirData,
    restoreSession,
    refreshToken,
    clearSession,
    logout,
    getAuthHeader,
  }
})
